import React, { useState, useRef } from 'react';
import { cn } from 'lib/utils';
import { Icon } from 'components/ui';

interface AvatarUploaderProps {
  currentAvatarUrl?: string;
  onUploadComplete: (file: File) => Promise<void>;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  disabled?: boolean;
  userName?: string;
  onRemoveImage?: () => void;
  removed?: boolean; // Add prop to indicate avatar has been removed
}

const sizeClasses = {
  sm: 'w-16 h-16',
  md: 'w-24 h-24',
  lg: 'w-32 h-32',
};

// Helper function to get first character of user's name
const getInitial = (userName?: string): string => {
  if (!userName || !userName.trim()) {
    return '?';
  }
  return userName.trim().charAt(0).toUpperCase();
};

const AvatarUploader: React.FC<AvatarUploaderProps> = ({
  currentAvatarUrl,
  onUploadComplete,
  size = 'md',
  className,
  disabled = false,
  userName,
  onRemoveImage,
  removed = false,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const maxSize = 2 * 1024 * 1024; // 2MB
  const acceptedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

  const validateFile = (file: File): boolean => {
    setError(null);

    if (!acceptedTypes.includes(file.type)) {
      setError('Please upload a valid image (JPG, PNG, WebP, or GIF)');
      return false;
    }

    if (file.size > maxSize) {
      setError('Image must be smaller than 2MB');
      return false;
    }

    return true;
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!validateFile(file)) return;

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Upload
    setIsUploading(true);
    try {
      await onUploadComplete(file);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload avatar');
      setPreviewUrl(null);
    } finally {
      setIsUploading(false);
    }
  };

  const openFileDialog = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const displayUrl = removed ? null : previewUrl || currentAvatarUrl;

  return (
    <div className={cn('flex flex-col items-center gap-3', className)}>
      {/* Avatar Preview */}
      <div className="relative">
        <div
          className={cn(
            'rounded-full overflow-hidden border-2 border-gray-200 bg-gray-100 flex items-center justify-center',
            sizeClasses[size],
            disabled && 'opacity-50',
          )}
        >
          {displayUrl ? (
            <img src={displayUrl} alt="User avatar" className="w-full h-full object-cover" />
          ) : (
            <span
              className={cn(
                'font-semibold text-gray-600 select-none',
                size === 'sm' ? 'text-lg' : size === 'md' ? 'text-2xl' : 'text-3xl',
              )}
            >
              {getInitial(userName)}
            </span>
          )}
        </div>

        {/* Upload Button Overlay */}
        {!disabled && (
          <button
            type="button"
            onClick={openFileDialog}
            disabled={isUploading}
            className={cn(
              'absolute bottom-0 right-0 p-2 bg-primary rounded-full shadow-lg',
              'hover:bg-primary/90 transition-colors',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            )}
            title="Upload new avatar"
          >
            {isUploading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Icon type="upload" className="w-4 h-4 text-white" />
            )}
          </button>
        )}
      </div>

      {/* Upload Instructions */}
      {!disabled && (
        <div className="text-center">
          <div className="flex flex-col items-center gap-2">
            <button
              type="button"
              onClick={openFileDialog}
              disabled={isUploading}
              className="text-sm font-medium text-primary hover:underline disabled:opacity-50"
            >
              {isUploading
                ? 'Uploading...'
                : currentAvatarUrl && !removed
                ? 'Change photo'
                : 'Upload photo'}
            </button>

            {/* Remove Image Button - Only show when there's an avatar and hasn't been removed */}
            {currentAvatarUrl && !removed && onRemoveImage && (
              <button
                type="button"
                onClick={onRemoveImage}
                className="text-xs text-red-600 hover:text-red-700 hover:underline disabled:opacity-50"
              >
                Remove photo
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">JPG, PNG, WebP or GIF (max 2MB)</p>
        </div>
      )}

      {/* Error Message */}
      {error && <p className="text-xs text-red-600 text-center">{error}</p>}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFileSelect}
        disabled={disabled || isUploading}
        className="hidden"
      />
    </div>
  );
};

export { AvatarUploader };
